import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createOrderWriter } from "@/lib/order-writes";
import { Columns3, List, Plus, Rows4, X } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import type { TaskQuery, TaskWithFlags } from "@desk/contracts";
import type { TaskGroup } from "@/concepts/types";
import { Segmented } from "@/components/ui/misc";
import { Button } from "@/components/ui/button";
import { useHierarchy, useTaskList, type Hierarchy } from "@/data/hooks";
import { useApi } from "@/data/context";
import { invalidateTaskData } from "@/data/cache";
import {
  activeFilterCount,
  filtersFromParams,
  filtersToParams,
  filtersToQuery,
  isClosed,
  matchesFilters,
  sortTasks,
  type TaskFilters,
} from "@/lib/filters";
import { emptyKind } from "@/lib/empty-state";
import { useStored } from "@/lib/storage";
import { setParams, useLocation } from "@/router";
import { useConcept } from "@/features/concept-provider";
import { TaskListView } from "@/features/list/TaskListView";
import { BoardView } from "@/features/board/BoardView";
import { CompactView } from "@/features/compact/CompactView";
import { openQuickAdd } from "@/features/ui-state";
import { toast } from "sonner";
import { describeError } from "@/data/api";

export type ViewMode = "list" | "board" | "compact";
const VIEW_MODES: ViewMode[] = ["list", "board", "compact"];

export function useViewMode(allowBoard: boolean): [ViewMode, (v: ViewMode) => void] {
  const loc = useLocation();
  const [stored, setStored] = useStored<ViewMode>(`view:${loc.path}`, "list");
  const fromUrl = loc.params.get("view");
  // Remembered per location (the storage key is the route path); ?view= wins for shared links.
  const mode: ViewMode = !allowBoard ? "list" : VIEW_MODES.includes(fromUrl as ViewMode) ? (fromUrl as ViewMode) : stored;
  return [
    mode,
    (v) => {
      setStored(v);
      setParams({ view: v === "list" ? undefined : v });
    },
  ];
}

export function ViewToggle({ value, onChange }: { value: ViewMode; onChange: (v: ViewMode) => void }) {
  return (
    <Segmented
      ariaLabel="View"
      size="sm"
      value={value}
      onChange={onChange}
      options={[
        { value: "list", label: <List />, title: "List" },
        { value: "board", label: <Columns3 />, title: "Board" },
        { value: "compact", label: <Rows4 />, title: "Compact" },
      ]}
    />
  );
}

export interface CollectionOptions {
  base: Partial<TaskQuery>;
  defaults: TaskFilters;
  /** Which unsynced local creates belong here. */
  matchCreate: (t: TaskWithFlags) => boolean;
  /** Hide snoozed tasks (attention lists such as Inbox). */
  hideSnoozed?: boolean;
  /** Extra client-side constraint (e.g. inbox = no area). */
  extra?: (t: TaskWithFlags) => boolean;
}

export function useCollection(opts: CollectionOptions) {
  const loc = useLocation();
  const filters = useMemo(() => filtersFromParams(loc.params, opts.defaults), [loc.params, opts.defaults]);
  const setFilters = (f: TaskFilters) => setParams({ ...filtersToParams(f, opts.defaults), view: loc.params.get("view") ?? undefined });
  const query = useMemo(() => filtersToQuery(filters, opts.base), [filters, opts.base]);
  const list = useTaskList(query, opts.matchCreate);
  const [manualOrder, setManualOrder] = useState<string[] | null>(null);
  const manualPending = useRef(false);
  useEffect(() => { if (!manualPending.current) setManualOrder(null); }, [list.data]);

  const tasks = useMemo(() => {
    let out = list.tasks.filter((t) => matchesFilters(t, filters) && (!opts.extra || opts.extra(t)));
    if (opts.hideSnoozed) out = out.filter((t) => !t.flags.isSnoozed);
    out = sortTasks(out, filters.sort);
    if (manualOrder && filters.sort === "manual") {
      const idx = new Map(manualOrder.map((id, i) => [id, i]));
      out = [...out].sort((a, b) => (idx.get(a.id) ?? 1e9) - (idx.get(b.id) ?? 1e9));
    }
    return out;
  }, [list.tasks, filters, opts, manualOrder]);

  const labels = useMemo(() => {
    const s = new Set<string>();
    list.tasks.forEach((t) => t.labels.forEach((l) => s.add(l)));
    return [...s].sort();
  }, [list.tasks]);

  const filtered = activeFilterCount(filters, opts.defaults) > 0;
  const clearFilters = () => setFilters({ ...opts.defaults, sort: filters.sort, showCompleted: filters.showCompleted });
  return {
    filters,
    setFilters,
    tasks,
    labels,
    isLoading: list.isLoading,
    error: list.error,
    setManualOrder: (ids: string[], pending = false) => { manualPending.current = pending; setManualOrder(ids); },
    /** For `CollectionBody`: whether the list has answered, and whether user filters narrow it. */
    status: { loaded: !!list.data, error: list.error, filtered, clearFilters },
    retry: () => void list.refetch(),
  };
}

export type GroupBy = "none" | "project" | "space";

export function buildGroups(
  tasks: TaskWithFlags[],
  groupBy: GroupBy,
  h: Hierarchy,
  completed: { collapsed: boolean; onToggle: () => void },
): TaskGroup[] {
  const open = tasks.filter((t) => !isClosed(t));
  const closed = tasks.filter(isClosed);
  const groups: TaskGroup[] = [];
  if (groupBy === "none") groups.push({ key: "open", label: null, tasks: open });
  else {
    const keyOf = (t: TaskWithFlags) => (groupBy === "project" ? t.projectId : t.spaceId) ?? "";
    const order: string[] = [];
    const buckets = new Map<string, TaskWithFlags[]>();
    for (const t of open) {
      const k = keyOf(t);
      if (!buckets.has(k)) {
        buckets.set(k, []);
        order.push(k);
      }
      buckets.get(k)!.push(t);
    }
    // Direct tasks first (no header), then containers in sidebar order.
    const rank = (k: string) => {
      if (!k) return -1;
      const list = groupBy === "project" ? h.projects : h.spaces;
      const i = list.findIndex((x) => x.id === k);
      return i === -1 ? 1e6 : i;
    };
    order.sort((a, b) => rank(a) - rank(b));
    for (const k of order) {
      const label = !k ? null : groupBy === "project" ? (h.project(k)?.name ?? "Project") : (h.space(k)?.name ?? "Space");
      groups.push({ key: k || "direct", label, tasks: buckets.get(k)! });
    }
  }
  if (closed.length) {
    groups.push({
      key: "completed",
      label: "Completed",
      tasks: closed,
      collapsible: true,
      collapsed: completed.collapsed,
      onToggle: completed.onToggle,
    });
  }
  return groups;
}

export function CollectionBody({
  tasks,
  view,
  filters,
  setFilters,
  groupBy = "none",
  location,
  emptyMessage,
  newTaskPreset,
  ariaLabel,
  onManualOrder,
  status,
}: {
  tasks: TaskWithFlags[];
  view: ViewMode;
  filters: TaskFilters;
  setFilters: (f: TaskFilters) => void;
  groupBy?: GroupBy;
  location?: (t: TaskWithFlags) => string | null;
  emptyMessage: string;
  newTaskPreset?: Parameters<typeof openQuickAdd>[0];
  ariaLabel: string;
  onManualOrder?: (ids: string[], pending?: boolean) => void;
  status?: { loaded: boolean; error: unknown; filtered: boolean; clearFilters: () => void };
}) {
  const { concept } = useConcept();
  const h = useHierarchy();
  const api = useApi();
  const qc = useQueryClient();
  const writeOrder = useMemo(() => createOrderWriter(api, "tasks"), [api]);
  const [completedCollapsed, setCompletedCollapsed] = useStored("completedCollapsed", false);
  const kind = status ? emptyKind(status) : "empty";
  // Loading stays blank and a failed load leaves the message to ScreenError above, so neither claims the list is empty.
  const empty =
    kind === "loading" || kind === "error" ? (
      <div className="h-24" aria-busy={kind === "loading"} />
    ) : kind === "filtered" ? (
      <concept.EmptyState
        message="No tasks match these filters."
        action={
          <Button size="sm" variant="outline" onClick={status?.clearFilters}>
            <X />
            Clear filters
          </Button>
        }
      />
    ) : (
      <concept.EmptyState
        message={emptyMessage}
        action={
          <Button size="sm" variant="outline" onClick={() => openQuickAdd(newTaskPreset)}>
            <Plus />
            New task
          </Button>
        }
      />
    );
  if (view === "board") {
    return <BoardView tasks={tasks} showClosed={filters.showCompleted} location={location} empty={tasks.length === 0 ? empty : undefined} />;
  }
  const groups = buildGroups(tasks, filters.sort === "manual" ? "none" : groupBy, h, {
    collapsed: completedCollapsed,
    onToggle: () => setCompletedCollapsed(!completedCollapsed),
  });
  const reorder =
    filters.sort === "manual"
      ? (ids: string[]) => writeOrder(ids, tasks.map((t) => t.id), {
          apply: (next, pending) => onManualOrder?.(next, pending),
          write: (next) => api.reorderTasks(next),
          onError: (error) => toast.error("Couldn't save the order", { description: describeError(error) }),
          onSettled: () => void invalidateTaskData(qc),
        })
      : undefined;
  const View = view === "compact" ? CompactView : TaskListView;
  return (
    <View
      groups={groups}
      ariaLabel={ariaLabel}
      location={location}
      empty={empty}
      onReorder={reorder ? (ids) => void reorder(ids) : undefined}
      footer={
        <button
          type="button"
          onClick={() => setFilters({ ...filters, showCompleted: !filters.showCompleted })}
          className="rounded-md px-1.5 py-1 text-[13px] text-subtle hover:bg-hover hover:text-foreground"
        >
          {filters.showCompleted ? "Hide completed" : "Show completed"}
        </button>
      }
    />
  );
}

export function ScreenError({ error, onRetry }: { error: unknown; onRetry?: () => void }): ReactNode {
  if (!error) return null;
  return (
    <div role="alert" className="mb-4 flex items-center gap-3 rounded-md bg-urgent-soft py-1.5 pl-3 pr-1.5 text-[13px]">
      <span className="min-w-0 flex-1">{describeError(error)}</span>
      {onRetry && (
        <Button size="xs" variant="outline" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}
